import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Image, X, Loader2, Check, CheckCheck, ChevronLeft, Camera } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '@/config/api';

// Group messages by date
const groupByDate = (msgs) => {
  const groups = [];
  let currentDate = '';
  msgs.forEach(msg => {
    const date = new Date(msg.created_at).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'short' });
    if (date !== currentDate) {
      currentDate = date;
      groups.push({ type: 'date', date });
    }
    groups.push({ type: 'message', ...msg });
  });
  return groups;
};

export default function AppMessages() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const pollRef = useRef(null);
  const initializedRef = useRef(false);
  const fileInputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 80);
  }, []);

  const fetchMessages = useCallback(async (convId) => {
    try {
      const r = await fetch(`${API_URL}/api/conversations/${convId}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (r.ok) {
        const data = await r.json();
        setMessages(prev => {
          if (prev.length !== data.length || (data.length > 0 && prev[prev.length - 1]?.message_id !== data[data.length - 1]?.message_id)) {
            return data;
          }
          return prev;
        });
      }
    } catch (e) { console.debug('Message poll failed:', e); }
  }, [token]);

  const initConversation = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!r.ok) throw new Error('Failed to connect');
      const conv = await r.json();
      setConversation(conv);
      await fetchMessages(conv.conversation_id);
      pollRef.current = setInterval(() => fetchMessages(conv.conversation_id), 3000);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, fetchMessages]);

  useEffect(() => {
    if (!token || initializedRef.current) return;
    initializedRef.current = true;
    initConversation();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [initConversation, token]);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG, PNG, GIF, WEBP allowed'); return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/chat/upload-image`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include', body: fd
      });
      if (!r.ok) throw new Error('Upload failed');
      const data = await r.json();
      setPendingImage({ url: `${API_URL}${data.image_url}`, name: data.filename, preview: URL.createObjectURL(file) });
    } catch (e) { toast.error(e.message); }
    finally { setUploadingImage(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const removePendingImage = () => {
    if (pendingImage?.preview) URL.revokeObjectURL(pendingImage.preview);
    setPendingImage(null);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !pendingImage) || !conversation) return;
    setSending(true);
    try {
      const r = await fetch(`${API_URL}/api/conversations/${conversation.conversation_id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ content: newMessage.trim() || '', image_url: pendingImage?.url || null, image_name: pendingImage?.name || null })
      });
      if (!r.ok) throw new Error('Failed to send');
      const sent = await r.json();
      setMessages(prev => [...prev, sent]);
      setNewMessage('');
      removePendingImage();
      scrollToBottom();
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  const getImageUrl = (url) => url?.startsWith('/api') ? `${API_URL}${url}` : url;

  const grouped = groupByDate(messages);

  const quickReplies = [
    { text: 'Where is my order?', icon: '📦' },
    { text: 'Dietary requirements', icon: '🥗' },
    { text: 'Delivery issue', icon: '🚚' },
    { text: 'General enquiry', icon: '💬' },
  ];

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-5">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Sign in to chat</h2>
          <button onClick={() => navigate('/auth')} className="bg-primary text-white px-6 py-2 rounded-xl font-medium">Sign In</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-[#f0f0f0]" data-testid="app-messages">
      {/* Header — iMessage style */}
      <div className="flex-shrink-0 bg-[#f8f8f8]/95 backdrop-blur-xl border-b border-gray-200/60 z-40">
        <div className="flex items-center gap-3 px-4 py-3 max-w-2xl mx-auto">
          <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center text-primary -ml-1">
            <ChevronLeft className="h-6 w-6" />
          </button>
          <div className="w-10 h-10 bg-gradient-to-br from-primary to-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white text-sm font-bold">GC</span>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold text-base text-gray-900">GC3H Support</h1>
            <p className="text-xs text-green-600 flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
              Online
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={containerRef} className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-3 py-4 pb-4" style={{ minHeight: '100%' }}>
          {loading ? (
            <div className="flex items-center justify-center h-full py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary/50" />
            </div>
          ) : messages.length === 0 ? (
            /* Empty state */
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 bg-gradient-to-br from-primary/20 to-amber-100 rounded-full flex items-center justify-center mb-4">
                <Send className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-1">Need help?</h3>
              <p className="text-gray-500 text-sm mb-6 text-center px-8">Send us a message and we'll get back to you quickly</p>
              <div className="grid grid-cols-2 gap-2 w-full max-w-xs">
                {quickReplies.map((qr) => (
                  <motion.button
                    key={qr.text}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setNewMessage(qr.text)}
                    className="bg-white rounded-2xl px-3 py-3 text-left shadow-sm border border-gray-100 active:bg-gray-50"
                  >
                    <span className="text-lg mb-1 block">{qr.icon}</span>
                    <span className="text-xs font-medium text-gray-700">{qr.text}</span>
                  </motion.button>
                ))}
              </div>
            </div>
          ) : (
            /* Messages list */
            <div className="space-y-1">
              {grouped.map((item, idx) => {
                if (item.type === 'date') {
                  return (
                    <div key={`date-${idx}`} className="flex justify-center py-3">
                      <span className="bg-gray-300/50 backdrop-blur text-gray-600 text-[11px] font-medium px-3 py-1 rounded-full">
                        {item.date}
                      </span>
                    </div>
                  );
                }

                const msg = item;
                const isOwn = msg.sender_id === user?.user_id;
                const isRead = msg.read_count > 1;
                const time = new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                return (
                  <motion.div
                    key={msg.message_id}
                    initial={{ opacity: 0, y: 6, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.2 }}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-0.5`}
                  >
                    <div className={`max-w-[78%] relative ${isOwn ? 'order-1' : 'order-1'}`}>
                      {/* Bubble */}
                      <div className={`px-3 py-2 ${
                        isOwn
                          ? 'bg-[#007AFF] text-white rounded-[18px] rounded-br-[4px]'
                          : 'bg-white text-gray-900 rounded-[18px] rounded-bl-[4px] shadow-[0_1px_2px_rgba(0,0,0,0.08)]'
                      }`}>
                        {!isOwn && msg.sender_name && msg.sender_role !== 'customer' && (
                          <p className="text-[11px] font-semibold text-primary mb-0.5">{msg.sender_name}</p>
                        )}
                        {msg.image_url && (
                          <img
                            src={getImageUrl(msg.image_url)}
                            alt="Attachment"
                            className="max-w-full rounded-xl mb-1.5 max-h-48 cursor-pointer"
                            onClick={() => window.open(getImageUrl(msg.image_url), '_blank')}
                          />
                        )}
                        {msg.content && (
                          <p className="text-[15px] leading-snug whitespace-pre-wrap break-words">{msg.content}</p>
                        )}
                        <div className={`flex items-center gap-1 mt-0.5 ${isOwn ? 'justify-end' : ''}`}>
                          <span className={`text-[10px] ${isOwn ? 'text-white/60' : 'text-gray-400'}`}>{time}</span>
                          {isOwn && (
                            isRead
                              ? <CheckCheck className="h-3 w-3 text-white/60" />
                              : <Check className="h-3 w-3 text-white/60" />
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* Pending Image */}
      <AnimatePresence>
        {pendingImage && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            className="flex-shrink-0 bg-white border-t border-gray-200 px-4 py-2"
          >
            <div className="max-w-2xl mx-auto flex items-center gap-3">
              <img src={pendingImage.preview} alt="Preview" className="h-14 w-14 rounded-xl object-cover" />
              <span className="text-sm text-gray-600 flex-1 truncate">{pendingImage.name}</span>
              <button onClick={removePendingImage} className="p-1.5 rounded-full hover:bg-gray-100"><X className="h-4 w-4 text-gray-500" /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input bar — iMessage style */}
      <div className="flex-shrink-0 bg-[#f8f8f8]/95 backdrop-blur-xl border-t border-gray-200/60 pb-16" style={{ paddingBottom: 'max(64px, calc(env(safe-area-inset-bottom) + 56px))' }}>
        <form onSubmit={handleSend} className="max-w-2xl mx-auto flex items-end gap-2 px-3 py-2">
          <input type="file" ref={fileInputRef} onChange={handleImageSelect} accept="image/*" className="hidden" />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingImage || sending}
            className="w-9 h-9 flex items-center justify-center text-primary flex-shrink-0 mb-0.5"
            data-testid="attach-image-btn"
          >
            {uploadingImage ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-6 w-6" />}
          </button>
          <div className="flex-1 bg-white rounded-[20px] border border-gray-200 flex items-end overflow-hidden">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="iMessage"
              className="flex-1 px-4 py-2.5 text-[15px] bg-transparent border-none outline-none resize-none"
              disabled={sending}
              data-testid="message-input"
            />
          </div>
          <motion.button
            whileTap={{ scale: 0.85 }}
            type="submit"
            disabled={(!newMessage.trim() && !pendingImage) || sending}
            className="w-9 h-9 flex items-center justify-center flex-shrink-0 mb-0.5"
            data-testid="send-btn"
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
              (newMessage.trim() || pendingImage) ? 'bg-[#007AFF]' : 'bg-gray-300'
            }`}>
              <Send className="h-4 w-4 text-white ml-0.5" />
            </div>
          </motion.button>
        </form>
      </div>
    </div>
  );
}
